// frontend/packages/business-console/src/components/staff/StaffModeBanner.jsx
//
// Persistent visual indicator while an internal UpRyzze staff member is
// scoped into a client's data. Rendered at the very top of the layout
// (above the topbar) so it's unmissable.
//
// Intentionally yellow + non-dismissable — the whole point is to keep
// the operator from accidentally treating client data as their own.

import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import { useAuth } from "../../contexts/AuthContext";
import { api } from "@upryzze/services";

const BANNER_HEIGHT = 36;

export default function StaffModeBanner() {
  const { isImpersonating, businessId, setActiveBusinessId } = useAuth();
  const [businessName, setBusinessName] = useState("");

  // Resolve the active business name for display. We hit /api/staff/businesses
  // (cached client-side via sessionStorage) instead of the per-business
  // detail endpoint so the banner works even on the initial paint before
  // the page's own data has loaded.
  useEffect(() => {
    if (!isImpersonating || !businessId) {
      setBusinessName("");
      return;
    }

    let cached;
    try {
      cached = JSON.parse(sessionStorage.getItem("staffBusinessesCache") || "null");
    } catch {
      cached = null;
    }

    const fromCache = Array.isArray(cached)
      ? cached.find((b) => String(b._id) === String(businessId))
      : null;
    if (fromCache?.name) {
      setBusinessName(fromCache.name);
      return;
    }

    let alive = true;
    api
      .get("/staff/businesses", { headers: { "x-business-id": "" } })
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res?.data?.businesses) ? res.data.businesses : [];
        try {
          sessionStorage.setItem("staffBusinessesCache", JSON.stringify(list));
        } catch {
          // sessionStorage full or unavailable — caching is best-effort
        }
        const match = list.find((b) => String(b._id) === String(businessId));
        if (match?.name) setBusinessName(match.name);
      })
      .catch(() => {});

    return () => {
      alive = false;
    };
  }, [isImpersonating, businessId]);

  if (!isImpersonating || !businessId) return null;

  const handleExit = () => {
    setActiveBusinessId("");
    try {
      sessionStorage.removeItem("activeBusinessId");
    } catch {
      // ignore — clearing the selection in memory is what matters
    }
    // Send the operator back to the staff picker.
    if (typeof window !== "undefined") {
      window.location.assign("/staff");
    }
  };

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        // Fixed overlay at the very top. Layout reads BANNER_HEIGHT and
        // pushes the sidebar / topbars / main content down by that amount
        // when this banner is visible, so the banner can never clip page
        // chrome.
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: (t) => t.zIndex.appBar + 2,
        width: "100%",
        height: BANNER_HEIGHT,
        bgcolor: "#FACC15", // amber-400 — same tone as MUI warning but a bit hotter
        color: "rgba(0,0,0,0.87)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        px: 2,
        borderBottom: "2px solid #B45309",
        fontWeight: 600,
      }}
    >
      <VisibilityOutlinedIcon fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        STAFF MODE
      </Typography>
      <Typography variant="body2">
        Viewing as
        {businessName ? (
          <Box component="span" sx={{ fontWeight: 700, ml: 0.5 }}>
            {businessName}
          </Box>
        ) : (
          <Box component="span" sx={{ fontStyle: "italic", ml: 0.5 }}>
            (loading…)
          </Box>
        )}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={handleExit}
        sx={{
          ml: 1,
          color: "rgba(0,0,0,0.87)",
          borderColor: "rgba(0,0,0,0.6)",
          "&:hover": {
            borderColor: "rgba(0,0,0,0.87)",
            bgcolor: "rgba(0,0,0,0.04)",
          },
        }}
      >
        Exit
      </Button>
    </Box>
  );
}

export { BANNER_HEIGHT };
